<script setup lang="ts">
    import { computed, inject, ref } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useI18n } from 'vue-i18n'
    import { useChatbotStore } from 'composables'
    import PkAvatar from './PkAvatar.vue'
    import { ChatbotProfileContextKey } from './chatbotProfileContext'

    const props = defineProps<{ agentId: string }>()

    const { t: $t } = useI18n({ useScope: 'global' })

    const store = useChatbotStore(props.agentId)
    const { name: agentName, agentInterface } = storeToRefs(store)

    const profile = inject(ChatbotProfileContextKey, undefined)
    const user = computed(() => profile?.user.value ?? null)
    const canManage = computed(() => profile?.canManageProfile.value ?? false)
    const profileUrl = computed(() => profile?.profileUrl.value)

    const isSigningOut = ref(false)
    const errorMessage = ref<string>()

    async function onSignOut() {
        if (!profile) {
            return
        }
        isSigningOut.value = true
        try {
            await profile.signOut()
        } catch (error) {
            errorMessage.value =
                error instanceof Error ? error.message : $t('message.error')
        } finally {
            isSigningOut.value = false
        }
    }
</script>

<template>
    <div class="flex flex-col flex-1 min-h-0 overflow-y-auto">
        <!-- Hero: user identity -->
        <div
            class="flex flex-col items-center gap-sm pt-lg px-lg pb-md bg-surface-1 border-b border-surface-3">
            <PkAvatar
                modifiers="surface ring"
                class="w-64 h-64"
                :name="user?.name ?? agentName"
                :img-src="user?.image ?? agentInterface?.logo" />
            <div class="flex flex-col items-center gap-6 text-center">
                <strong
                    v-if="user?.name ?? agentName"
                    class="text-16 font-semibold text-word-1 leading-tight">
                    {{ user?.name ?? agentName }}
                </strong>
                <span
                    v-if="user?.email"
                    class="text-12 text-word-3 leading-none">
                    {{ user.email }}
                </span>
            </div>
        </div>

        <!-- Actions -->
        <div class="flex flex-col gap-sm p-md flex-1">
            <VvAlert
                v-if="errorMessage"
                modifiers="danger"
                :content="errorMessage" />

            <!-- Manage profile link card -->
            <a
                v-if="canManage && profileUrl"
                :href="profileUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-12 p-12 rounded-lg border border-surface-3 hover:border-surface-5 hover:bg-surface-1 transition-colors cursor-pointer no-underline">
                <div
                    class="w-40 h-40 rounded-md bg-surface-2 border border-surface-3 flex items-center justify-center shrink-0">
                    <VvIcon
                        name="ri:user-settings-line"
                        class="w-20 h-20 text-word-2" />
                </div>
                <div class="flex flex-col flex-1 min-w-0 gap-1">
                    <span class="text-14 font-medium text-word-1 leading-tight">
                        {{ $t('action.manageProfile') }}
                    </span>
                    <span class="text-12 text-word-3 leading-tight">
                        {{ $t('label.manageProfileDescription') }}
                    </span>
                </div>
                <VvIcon
                    name="ri:arrow-right-s-line"
                    class="w-16 h-16 text-word-4 shrink-0" />
            </a>
        </div>

        <!-- Footer: sign out -->
        <div class="px-md py-sm">
            <VvButton
                modifiers="secondary-danger"
                class="w-full"
                icon="ri:logout-box-line"
                :loading="isSigningOut"
                :disabled="isSigningOut"
                :label="$t('action.signOut')"
                @click="onSignOut" />
        </div>

        <slot />
    </div>
</template>
